
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import { useIsMobile } from "@/hooks/use-mobile";
import { EmptyState } from "@/components/EmptyState";
import { Newspaper, ArrowLeft, Calendar, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { apiClient } from "@/utils/apiClient";

interface NewsItem {
  id: string;
  title: string;
  short_description: string;
  content: string;
  status: string;
  expires_at: string | null;
  created_at: string;
  updated_at: string;
  created_by: string | null;
  updated_by: string | null;
}

const News: React.FC = () => {
  const isMobile = useIsMobile();
  const { id } = useParams();
  const navigate = useNavigate();
  const [news, setNews] = useState<NewsItem[]>([]);
  const [selectedNews, setSelectedNews] = useState<NewsItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch news list
  const fetchNews = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get<NewsItem[]>('news/');
      
      setNews(response);
    } catch (err) {
      setError('Failed to fetch news');
      console.error('Error fetching news:', err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch specific news item
  const fetchNewsDetail = async (newsId: string) => {
    try {
      setLoading(true);
      const response = await apiClient.get<NewsItem>(`news/${newsId}`);
      setSelectedNews(response);
    } catch (err) {
      setError('Failed to fetch news details');
      console.error('Error fetching news details:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchNewsDetail(id);
    } else {
      fetchNews();
    }
  }, [id]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleNewsClick = (newsItem: NewsItem) => {
    navigate(`/news/${newsItem.id}`);
  };

  const handleBackClick = () => {
    navigate('/news');
  };

  // News detail view
  if (id && selectedNews) {
    return (
      <div className="min-h-screen flex flex-col bg-background text-foreground">
        <Header />
        <main className="flex-1 container mx-auto px-4 py-6">
          <div className="mb-6">
            <Button
              variant="ghost"
              onClick={handleBackClick}
              className="mb-4"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to News
            </Button>
            
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl mb-4">{selectedNews.title}</CardTitle>
                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                  <div className="flex items-center gap-1">
                    <Calendar className="h-4 w-4" />
                    {formatDate(selectedNews.created_at)}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="mb-6">
                  <p className="text-lg text-muted-foreground">{selectedNews.short_description}</p>
                </div>
                <div 
                  className="prose prose-sm max-w-none"
                  dangerouslySetInnerHTML={{ __html: selectedNews.content }}
                />
              </CardContent>
            </Card>
          </div>
        </main>
      </div>
    );
  }

  // News list view
  return (
    <div className="min-h-screen flex flex-col bg-background text-foreground">
      <Header />
      <main className="flex-1 container mx-auto px-4 py-6">
        <div className="mb-6">
          <h1 className="text-3xl font-bold mb-2">Market News</h1>
          <p className="text-muted-foreground">Stay updated with the latest market developments</p>
        </div>

        {loading ? (
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <Card key={i}>
                <CardHeader>
                  <Skeleton className="h-6 w-3/4" />
                  <Skeleton className="h-4 w-1/2" />
                </CardHeader>
                <CardContent>
                  <Skeleton className="h-4 w-full mb-2" />
                  <Skeleton className="h-4 w-2/3" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : error ? (
          <EmptyState
            icon={<Newspaper className="h-12 w-12 text-muted-foreground" />}
            title="Error Loading News"
            description={error}
            action={
              <Button onClick={fetchNews} variant="outline">
                Try Again
              </Button>
            }
          />
        ) : news.length === 0 ? (
          <EmptyState
            icon={<Newspaper className="h-12 w-12 text-muted-foreground" />}
            title="No News Available"
            description="There are no news articles available at the moment."
          />
        ) : (
          <div className="space-y-4">
            {news.map((newsItem) => (
              <Card 
                key={newsItem.id} 
                className="cursor-pointer hover:shadow-md transition-shadow"
                onClick={() => handleNewsClick(newsItem)}
              >
                <CardHeader>
                  <CardTitle className="text-xl hover:text-primary transition-colors">
                    {newsItem.title}
                  </CardTitle>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Clock className="h-4 w-4" />
                    {formatDate(newsItem.created_at)} .. 
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground line-clamp-3">
                    {newsItem.short_description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default News;
